import React, { useState, useEffect } from "react"
import PropTypes from "prop-types"
import { Spinner } from "reactstrap"
import { getIncremental } from "./api"

const formatFecha = iso => {
  if (!iso) return "—"
  try {
    return new Date(iso).toLocaleString("es-CO", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    })
  } catch {
    return iso
  }
}

const formatNumber = n => (typeof n === "number" ? n.toLocaleString("es-CO") : n ?? "—")

const formatDensity = d => {
  if (d === null || d === undefined) return "—"
  const num = typeof d === "string" ? parseFloat(d) : d
  return isNaN(num) ? "—" : `${num.toFixed(2)}%`
}

const RECOMENDACIONES = {
  monitoreo_periodico: { label: "Monitoreo periódico", color: "#1e40af", bg: "#dbeafe" },
  revisar_pronto: { label: "Revisar pronto", color: "#92400e", bg: "#fef3c7" },
  accion_inmediata: { label: "Acción inmediata", color: "#991b1b", bg: "#fee2e2" },
  sin_cambios: { label: "Sin cambios", color: "#475569", bg: "#f1f5f9" },
}

const Delta = ({ antes, ahora, invertirColor = false }) => {
  const diff = (ahora ?? 0) - (antes ?? 0)
  if (diff === 0) {
    return <span style={{ color: "#64748b", fontSize: 12 }}>Sin cambio</span>
  }
  const positivo = diff > 0
  const esBueno = invertirColor ? !positivo : positivo
  const color = esBueno ? "#10b981" : "#dc2626"
  const arrow = positivo ? "▲" : "▼"
  return (
    <span style={{ color, fontSize: 13, fontWeight: 600 }}>
      {arrow} {formatNumber(Math.abs(diff))}
    </span>
  )
}

Delta.propTypes = {
  antes: PropTypes.number,
  ahora: PropTypes.number,
  invertirColor: PropTypes.bool,
}

const ComparacionRow = ({ label, antes, ahora, formatter = formatNumber, invertir = false }) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: "1fr auto auto auto",
      gap: 16,
      alignItems: "center",
      padding: "12px 16px",
      borderBottom: "1px solid #f1f5f9",
    }}
  >
    <div style={{ fontSize: 14, color: "#475569", fontWeight: 500 }}>{label}</div>
    <div style={{ textAlign: "right", color: "#94a3b8", fontSize: 14, minWidth: 90 }}>
      {formatter(antes)}
    </div>
    <div style={{ textAlign: "right", color: "#0f172a", fontSize: 16, fontWeight: 700, minWidth: 90 }}>
      {formatter(ahora)}
    </div>
    <div style={{ textAlign: "right", minWidth: 90 }}>
      <Delta antes={antes} ahora={ahora} invertirColor={invertir} />
    </div>
  </div>
)

ComparacionRow.propTypes = {
  label: PropTypes.string,
  antes: PropTypes.number,
  ahora: PropTypes.number,
  formatter: PropTypes.func,
  invertir: PropTypes.bool,
}

const TabIncremental = ({ nitemisor, prefijo }) => {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError("")
    getIncremental({ nitemisor, prefijo })
      .then(({ data }) => {
        if (!cancelled) setData(data)
      })
      .catch(err => {
        if (!cancelled) setError(err.response?.data?.message || "No se pudo cargar el análisis incremental.")
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [nitemisor, prefijo])

  if (loading) {
    return (
      <div style={{ padding: 60, textAlign: "center", color: "#64748b" }}>
        <Spinner color="primary" />
        <div className="mt-2">Comparando con último snapshot…</div>
      </div>
    )
  }

  if (error) {
    return (
      <div style={{ padding: 20, color: "#dc2626", background: "#fef2f2", borderRadius: 10 }}>
        <i className="mdi mdi-alert-circle me-2" />
        {error}
      </div>
    )
  }

  if (!data) return null

  if (!data.tiene_referencia) {
    return (
      <div
        style={{
          padding: 32,
          textAlign: "center",
          background: "#f8fafc",
          borderRadius: 12,
          border: "1px dashed #cbd5e1",
        }}
      >
        <i className="mdi mdi-history" style={{ fontSize: 48, color: "#94a3b8" }} />
        <h6 className="mt-3" style={{ color: "#0f172a" }}>Primer análisis</h6>
        <p style={{ color: "#64748b", margin: 0 }}>
          No hay snapshot previo para comparar. Este análisis se guardó como referencia y
          podrás ver deltas en la próxima revisión.
        </p>
      </div>
    )
  }

  const recom = RECOMENDACIONES[data.recomendacion] || {
    label: data.recomendacion || "—",
    color: "#475569",
    bg: "#f1f5f9",
  }

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
          padding: "12px 16px",
          background: "#f8fafc",
          borderRadius: 10,
        }}
      >
        <div>
          <div style={{ fontSize: 12, color: "#64748b", textTransform: "uppercase", fontWeight: 600 }}>
            Snapshot de referencia
          </div>
          <div style={{ fontSize: 14, color: "#0f172a", fontWeight: 600 }}>
            {formatFecha(data.fecha_referencia)}
          </div>
        </div>
        <span
          style={{
            padding: "6px 14px",
            borderRadius: 999,
            background: recom.bg,
            color: recom.color,
            fontSize: 13,
            fontWeight: 700,
            textTransform: "capitalize",
          }}
        >
          {recom.label}
        </span>
      </div>

      <div
        style={{
          border: "1px solid #e2e8f0",
          borderRadius: 12,
          background: "#fff",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr auto auto auto",
            gap: 16,
            padding: "10px 16px",
            background: "#f8fafc",
            borderBottom: "1px solid #e2e8f0",
            fontSize: 11,
            fontWeight: 600,
            color: "#64748b",
            textTransform: "uppercase",
            letterSpacing: 0.4,
          }}
        >
          <div>Métrica</div>
          <div style={{ textAlign: "right", minWidth: 90 }}>Antes</div>
          <div style={{ textAlign: "right", minWidth: 90 }}>Ahora</div>
          <div style={{ textAlign: "right", minWidth: 90 }}>Δ</div>
        </div>

        <ComparacionRow label="Gaps detectados" antes={data.gaps_antes} ahora={data.gaps_ahora} invertir />
        <ComparacionRow label="Folio máximo" antes={data.folio_max_antes} ahora={data.folio_max_ahora} />
        <ComparacionRow
          label="Densidad"
          antes={data.densidad_antes}
          ahora={data.densidad_ahora}
          formatter={formatDensity}
        />
      </div>

      <div className="row g-3 mt-3">
        <div className="col-md-6">
          <div
            style={{
              padding: 16,
              background: "#d1fae5",
              borderRadius: 12,
              borderLeft: "4px solid #10b981",
            }}
          >
            <div style={{ fontSize: 12, color: "#065f46", textTransform: "uppercase", fontWeight: 700, letterSpacing: 0.4 }}>
              Gaps resueltos
            </div>
            <div style={{ fontSize: 28, fontWeight: 700, color: "#065f46", marginTop: 4 }}>
              {formatNumber(data.gaps_resueltos)}
            </div>
          </div>
        </div>
        <div className="col-md-6">
          <div
            style={{
              padding: 16,
              background: "#fee2e2",
              borderRadius: 12,
              borderLeft: "4px solid #dc2626",
            }}
          >
            <div style={{ fontSize: 12, color: "#991b1b", textTransform: "uppercase", fontWeight: 700, letterSpacing: 0.4 }}>
              Gaps nuevos en rango
            </div>
            <div style={{ fontSize: 28, fontWeight: 700, color: "#991b1b", marginTop: 4 }}>
              {formatNumber((data.gaps_nuevos_rango || []).length)}
            </div>
          </div>
        </div>
      </div>

      {data.gaps_nuevos_rango && data.gaps_nuevos_rango.length > 0 && (
        <div className="mt-3">
          <h6 style={{ color: "#0f172a" }}>Folios nuevos faltantes</h6>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {data.gaps_nuevos_rango.slice(0, 200).map(folio => (
              <span
                key={folio}
                style={{
                  padding: "3px 10px",
                  background: "#fef2f2",
                  border: "1px solid #fecaca",
                  borderRadius: 6,
                  color: "#991b1b",
                  fontSize: 12,
                  fontFamily: "ui-monospace, SFMono-Regular, monospace",
                }}
              >
                {folio}
              </span>
            ))}
            {data.gaps_nuevos_rango.length > 200 && (
              <span style={{ fontSize: 12, color: "#64748b", padding: "3px 10px" }}>
                +{data.gaps_nuevos_rango.length - 200} más
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

TabIncremental.propTypes = {
  nitemisor: PropTypes.string.isRequired,
  prefijo: PropTypes.string.isRequired,
}

export default TabIncremental
